import React from 'react';
import { Check } from 'lucide-react';

const VatProgressTracker = ({
    currentStep,
    stepOrder,
    stepTitles,
    stepIcons,
    isStepCompleted,
    isStepActive,
    onStepClick,
    actualCurrentStep // The real position (may differ from currentStep during preview)
}) => {
    const getCurrentStepIndex = () => stepOrder.indexOf(currentStep);

    const steps = stepOrder.map((step, index) => ({
        id: index + 1,
        title: stepTitles[step].replace(/^Steg \d+: /, ''),
        description: getStepDescription(step),
        icon: stepIcons[step],
        key: step
    }));

    function getStepDescription(step) {
        const descriptions = {
            'instructions': 'Läs instruktioner och förbered',
            'review': 'Granska momsuppgifter',
            'submission': 'Skicka till Skatteverket',
            'payment': 'Hantera betalning',
            'record': 'Slutför bokföring'
        };
        return descriptions[step] || '';
    }

    const isStepAccessible = (step) => {
        // All steps are clickable for preview — the parent's handleStepClick
        // controls which ones actually do something based on furthest reached
        return onStepClick != null;
    };

    const getStatus = (step) => {
        if (isStepCompleted(step.key)) return 'completed';
        if (isStepActive(step.key)) return 'current';
        return 'pending';
    };

    return (
        <>
            <style>{`
                .vat-progress-tracker {
                    background: white;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e5e7eb;
                }

                .vat-step-indicator {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    font-weight: 600;
                    font-size: 0.875rem;
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                }

                .vat-step-indicator.completed {
                    background: var(--bs-success);
                    color: white;
                }

                .vat-step-indicator.current {
                    background: var(--bs-primary);
                    color: white;
                    animation: vat-pulse 2s infinite;
                }

                .vat-step-indicator.pending {
                    background: #f3f4f6;
                    color: #6b7280;
                }

                .vat-step-connector {
                    flex: 1;
                    height: 2px;
                    background: #e5e7eb;
                    margin: 0 0.75rem;
                    transition: background 0.3s ease;
                }

                .vat-step-connector.completed {
                    background: var(--bs-primary);
                }

                @keyframes vat-pulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(68, 129, 78, 0.4);
                    }
                    70% {
                        box-shadow: 0 0 0 10px rgba(68, 129, 78, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(68, 129, 78, 0);
                    }
                }

                .vat-step-block {
                    cursor: default;
                }

                .vat-step-block.accessible {
                    cursor: pointer;
                }

                .vat-step-block.accessible:hover .vat-step-label {
                    color: var(--bs-primary);
                }

                .vat-step-label {
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: #1e293b;
                    transition: color 0.2s ease;
                }

                .vat-step-sublabel {
                    font-size: 0.75rem;
                    color: #6b7280;
                }

                .vat-step-block.completed .vat-step-label {
                    color: #065f46;
                }

                .vat-step-block.current .vat-step-label {
                    color: var(--bs-primary);
                }

                .vat-step-block.pending .vat-step-label {
                    color: #9ca3af;
                }

                .vat-step-block.pending .vat-step-sublabel {
                    color: #d1d5db;
                }

                @media (max-width: 768px) {
                    .vat-steps-row {
                        flex-direction: column !important;
                        gap: 0.5rem;
                    }

                    .vat-step-connector {
                        display: none;
                    }

                    .vat-step-block {
                        background: #f8fafc;
                        border-radius: 8px;
                        border: 1px solid #e5e7eb;
                        padding: 0.75rem 1rem;
                    }
                }
            `}</style>

            <div className="vat-progress-tracker">
                <div className="d-flex align-items-center justify-content-between vat-steps-row">
                    {steps.map((step, index) => {
                        const status = getStatus(step);
                        const accessible = isStepAccessible(step.key);
                        const IconComponent = step.icon;

                        return (
                            <React.Fragment key={step.key}>
                                <div
                                    className={`d-flex align-items-center vat-step-block ${status} ${accessible ? 'accessible' : ''}`}
                                    onClick={() => accessible && onStepClick && onStepClick(step.key)}
                                >
                                    <div className={`vat-step-indicator ${status}`}>
                                        {status === 'completed' ? (
                                            <Check size={20} />
                                        ) : (
                                            <IconComponent size={18} />
                                        )}
                                    </div>
                                    <div className="ms-3">
                                        <div className="vat-step-label">{step.title}</div>
                                        <div className="vat-step-sublabel">{step.description}</div>
                                    </div>
                                </div>

                                {index < steps.length - 1 && (
                                    <div className={`vat-step-connector ${status === 'completed' ? 'completed' : ''}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default VatProgressTracker;
